import { z } from "zod";
import seedPipelineWorkflowJson from "./pipeline-workflow-default.json" with { type: "json" };
import { auctionLivenessFromSourceFields, isAuctionStillEnrichable } from "./auction-liveness.js";
import {
  DEFAULT_AUCTION_WINDOW_POLICY,
  classifyAuctionTiming,
  decideAuctionEligibility,
  hasSuccessfulSpLookup,
  isAuctionRefreshDue,
  isAuctionSourceCheckDue,
  nextAuctionSourceCheckAt,
  nextRefreshWakeup,
  normalizeAuctionWindowPolicy,
  resolveAuctionDate,
  type AuctionWindowPolicy
} from "./auction-window.js";
import { hasSmartSkipResult } from "./close-skip-lead.js";
import {
  calculatePropertyEquity,
  equityPolicyFromSteps,
  equityProvenanceFromSourceFields,
  finiteNumber,
  resolvePropertyValue,
  shouldRecomputeEquity,
  type EquityPolicy
} from "./property-equity.js";
import {
  propertyObservationFromSourceFields,
  propertyObservationToolOutcome
} from "./property-observation.js";
import {
  closeEligibilityPolicyFromWorkflow,
  decideCloseEligibility
} from "./close-eligibility.js";
import { isBusinessOwnerText, isPersonOwner } from "./owner-kind.js";
import { selectOwnerLookupTool } from "./owner-lookup-runtime.js";
import {
  classifyObituaryOutcome,
  obituaryPlannerFailureClass
} from "./obituary-outcome.js";
import {
  automaticObituaryPropertyMatch,
  isObituarySearchToolId,
  obituaryStepPolicyFromWorkflow
} from "./obituary-steps.js";
import {
  ECOURTS_AUCTION_DATE_MISS,
  ECOURTS_AUCTION_NO_RESULT,
  ECOURTS_BROWSER_LEASE_EXPIRED,
  ECOURTS_COLLECTION_PARTIAL,
  ECOURTS_MISSING_REQUIRED_PDFS,
  ECOURTS_PAGE_INACCESSIBLE,
  isTransientEcourtsPlvEvidenceClass
} from "./ecourts-auction-plv.js";
import { isPipelineServiceFailureClass } from "./pipeline-tool-status.js";
import {
  heirEstimatePolicyFromWorkflow,
  heirEstimateStatusFromFacts
} from "./heir-estimate.js";
import {
  isRegisteredToolId,
  pipelineToolAvailability,
  pipelineToolDefinition,
  pipelineWorkflowRegistryIssues,
  type RegisteredToolId
} from "./pipeline-tool-registry.js";
import {
  pipelineRequiredRoleMissingMessage,
  pipelineStepForRole,
  pipelineStepHasFallbackGroup,
  pipelineStepRole,
  pipelineWorkflowRoleIssues,
  type PipelineRequiredRole
} from "./pipeline-workflow-roles.js";

export {
  PIPELINE_REQUIRED_ROLES,
  pipelineRequiredRoleMissingMessage,
  pipelineStepForRole,
  pipelineStepHasFallbackGroup,
  pipelineStepRole,
  pipelineWorkflowRoleIssues,
  type PipelineRequiredRole,
  type PipelineRoleStep
} from "./pipeline-workflow-roles.js";

export {
  PIPELINE_TOOL_REGISTRY,
  REGISTERED_TOOL_IDS,
  executableBatchDrainHandlerIds,
  isRegisteredToolId,
  pipelineStepLaneAvailability,
  pipelineToolAvailability,
  pipelineToolDefinition,
  pipelineToolExecutorHandlerId,
  pipelineToolTitle,
  pipelineToolsInFallbackGroup,
  pipelineWorkflowRegistryIssues,
  selectConfiguredFallback,
  workflowUsesFallbackGroup,
  type PipelineStepLaneAvailability,
  type PipelineToolAvailability,
  type PipelineToolContract,
  type PipelineToolDefinition,
  type PipelineToolExecutorCapability,
  type RegisteredToolId
} from "./pipeline-tool-registry.js";

export const PIPELINE_WORKFLOW_CONTENT_TYPE = "application/vnd.county-pipeline.workflow" as const;
export const PIPELINE_WORKFLOW_VERSION = 1 as const;

export const PIPELINE_STEP_KINDS = ["ingest", "gate", "enrichment", "destination", "terminal"] as const;
export type PipelineStepKind = (typeof PIPELINE_STEP_KINDS)[number];

export const PIPELINE_MISS_BEHAVIORS = ["block", "hold"] as const;
export type PipelineMissBehavior = (typeof PIPELINE_MISS_BEHAVIORS)[number];

export const PipelineStepGateSchema = z.object({
  auctionWindowDays: z.number().int().positive().optional(),
  dateRefreshCadence: z.enum(["weekly", "never"]).optional(),
  deceasedRequired: z.boolean().optional(),
  equityMinUsd: z.number().optional(),
  obituaryRequired: z.boolean().optional(),
  obituaryAmbiguityRecovery: z.enum(["seek_allowed_evidence", "insufficient_evidence"]).optional(),
  marketValuePct: z.number().positive().optional(),
  loanValuePct: z.number().positive().optional()
});
export type PipelineStepGate = z.infer<typeof PipelineStepGateSchema>;

export const PipelineStepSchema = z.object({
  id: z.string().trim().min(1).max(80),
  title: z.string().trim().min(1).max(80),
  kind: z.enum(PIPELINE_STEP_KINDS),
  tools: z.array(z.string().trim().min(1).max(80)).max(12),
  miss: z.enum(PIPELINE_MISS_BEHAVIORS).default("block"),
  boardVisible: z.boolean().optional(),
  gate: PipelineStepGateSchema.optional()
});
export type PipelineStep = Omit<z.infer<typeof PipelineStepSchema>, "tools"> & {
  tools: RegisteredToolId[];
};

export const PipelineWorkflowDocumentSchema = z.object({
  contentType: z.literal(PIPELINE_WORKFLOW_CONTENT_TYPE),
  version: z.literal(PIPELINE_WORKFLOW_VERSION),
  id: z.string().trim().min(1),
  retiredStepIds: z.array(z.string().trim().min(1).max(80)).max(20).optional(),
  steps: z.array(PipelineStepSchema).min(1).max(40)
}).superRefine((document, context) => {
  const ids = document.steps.map((step) => step.id);
  if (new Set(ids).size !== ids.length) {
    context.addIssue({ code: "custom", message: "pipeline steps require unique ids" });
  }
  for (const message of pipelineWorkflowRegistryIssues(document)) {
    context.addIssue({ code: "custom", message });
  }
  for (const message of pipelineWorkflowRoleIssues(document)) {
    context.addIssue({ code: "custom", message });
  }
});
export type PipelineWorkflowDocument = Omit<z.infer<typeof PipelineWorkflowDocumentSchema>, "steps"> & {
  steps: PipelineStep[];
};

export type WorkflowHopAction =
  | "tree_ingest"
  | "auction_date_refresh"
  | "ecourts_upsert"
  | "reportall"
  | "attom_owner"
  | "smartskip"
  | "avm"
  | "zillow"
  | "plv"
  | "equity"
  | "close_create"
  | "obituary"
  | "intestacy"
  | "status_ready"
  | "status_hold"
  | "none";

export type WorkflowHopDecision = {
  stepId: string;
  toolId: RegisteredToolId | null;
  action: WorkflowHopAction;
  state: string;
  failureClass: string | null;
  nextWakeup?: string | null;
};

export type WorkflowHouseFacts = {
  conveyorState?: string | null;
  conveyorFailureClass?: string | null;
  skipStatus?: string | null;
  auctionDate?: unknown;
  sourceFields?: unknown;
  ownerRaw?: unknown;
  firstName?: unknown;
  lastName?: unknown;
  skipPayload?: unknown;
  deceased?: unknown;
  zestimate?: unknown;
  attomAvm?: unknown;
  propertyValue?: unknown;
  plv?: unknown;
  estimatedEquity?: unknown;
  closeLeadId?: string | null;
  obituaryMatchStatus?: unknown;
  obituaryUrl?: unknown;
  intestacyTree?: unknown;
  intestacyStatus?: unknown;
  toolMisses?: unknown;
  auctionDateRefreshedAt?: unknown;
};

export function getActivePipelineWorkflow(): PipelineWorkflowDocument {
  return activePipelineWorkflow;
}

export function equityPolicyFromWorkflow(
  document: PipelineWorkflowDocument = getActivePipelineWorkflow()
): EquityPolicy {
  return equityPolicyFromSteps(parsePipelineWorkflow(document).steps);
}

export function normalizePipelineWorkflow(value: unknown): PipelineWorkflowDocument {
  return modernizePipelineWorkflow(parsePipelineWorkflow(value));
}

export function setActivePipelineWorkflow(value: unknown): PipelineWorkflowDocument {
  activePipelineWorkflow = normalizePipelineWorkflow(value);
  return activePipelineWorkflow;
}

const CONFIRMED_OBIT = new Set(["confirmed_match", "operator_accepted"]);
const HOLD_OBIT = new Set(["no_match", "ambiguous_review"]);

const TOOL_TO_ACTION: Record<RegisteredToolId, WorkflowHopAction> = {
  tree_ingest: "tree_ingest",
  ecourts_auction_date: "auction_date_refresh",
  ecourts_case_upsert: "ecourts_upsert",
  brightdata_ecourts_auction_date: "auction_date_refresh",
  reportall_owner: "reportall",
  attom_owner: "attom_owner",
  smartskip: "smartskip",
  attom_avm: "avm",
  zillow_zestimate: "zillow",
  ecourts_sp_plv: "plv",
  equity_formula: "equity",
  close_create: "close_create",
  close_status: "none",
  obituary_google: "obituary",
  obituary_xai: "obituary",
  obituary_evidence: "obituary",
  obituary_identity: "obituary",
  nc_intestacy_text_tree: "intestacy"
};

export function parsePipelineWorkflow(value: unknown): PipelineWorkflowDocument {
  const parsed = PipelineWorkflowDocumentSchema.safeParse(value);
  if (!parsed.success) {
    throw new PipelineWorkflowSaveError("invalid_pipeline_workflow", formatWorkflowIssues(parsed.error));
  }
  return parsed.data as PipelineWorkflowDocument;
}

export const defaultPipelineWorkflow: PipelineWorkflowDocument = parsePipelineWorkflow(seedPipelineWorkflowJson);

let activePipelineWorkflow: PipelineWorkflowDocument = defaultPipelineWorkflow;

export function isPipelineBoardStep(step: PipelineStep): boolean {
  return step.boardVisible !== false;
}

export function pipelineBoardSteps(document: PipelineWorkflowDocument): PipelineStep[] {
  return parsePipelineWorkflow(document).steps.filter(isPipelineBoardStep);
}

export function pipelineBoardLaneIds(document: PipelineWorkflowDocument): string[] {
  return pipelineBoardSteps(document).map((step) => step.id);
}

/** Strip lanes listed in the seed document's retiredStepIds when hydrating a saved SQLite document. */
export function trimRetiredPipelineSteps(
  document: PipelineWorkflowDocument,
  seed: PipelineWorkflowDocument = defaultPipelineWorkflow
): PipelineWorkflowDocument {
  const retired = new Set(seed.retiredStepIds ?? []);
  if (retired.size === 0) return document;
  const steps = document.steps.filter((step) => !retired.has(step.id));
  if (steps.length === document.steps.length) return document;
  return parsePipelineWorkflow({ ...document, steps });
}

/** Drop Bright Data and the eCourts Upsert column. Projection stays a Retrieval-lane tool. */
export function modernizePipelineWorkflow(document: PipelineWorkflowDocument): PipelineWorkflowDocument {
  const seedById = new Map(defaultPipelineWorkflow.steps.map((step) => [step.id, step]));
  const steps: PipelineStep[] = [];
  for (const step of document.steps) {
    if (step.id === "ecourts_upsert") continue;
    const tools = step.tools.filter((id) => id !== "brightdata_ecourts_auction_date");
    let nextTools = step.id === "auction_window" && tools.length === 0 ? ["ecourts_auction_date" as const] : tools;
    if (step.id === "auction_window" && !nextTools.includes("ecourts_case_upsert")) {
      nextTools = [...nextTools, "ecourts_case_upsert"];
    }
    let title = step.title;
    if (step.id === "auction_window") title = "eCourts Retrieval";
    if (step.id === "plv") title = "eCourts Analysis";
    const next = { ...step, title, tools: nextTools };
    if (seedById.get(step.id)?.boardVisible === false) next.boardVisible = false;
    steps.push(next);
  }
  if (!steps.some((step) => step.id === "waiting") && seedById.has("waiting")) {
    const plvAt = steps.findIndex((step) => step.id === "plv");
    steps.splice(plvAt >= 0 ? plvAt + 1 : steps.length, 0, seedById.get("waiting")!);
  }
  if (!steps.some((step) => step.id === "closed") && seedById.has("closed")) {
    steps.push(seedById.get("closed")!);
  }
  return parsePipelineWorkflow({ ...document, steps });
}

function formatWorkflowIssues(error: z.ZodError): string {
  const messages = [...new Set(error.issues.map((issue) => issue.message).filter(Boolean))];
  return messages.join(" ") || "invalid_pipeline_workflow";
}

export const PIPELINE_WORKFLOW_ACTIVE_SLOT = "pipeline" as const;

export const PIPELINE_WORKFLOW_SAVE_FAILURE_CLASSES = [
  "invalid_pipeline_workflow",
  "pipeline_workflow_conflict",
  "pipeline_workflow_save_failed",
  "pipeline_workflow_network_failure",
  "pipeline_workflow_load_failed"
] as const;
export type PipelineWorkflowSaveFailureClass = (typeof PIPELINE_WORKFLOW_SAVE_FAILURE_CLASSES)[number];

export type PipelineWorkflowRecord = {
  revision: number;
  document: PipelineWorkflowDocument;
};

export type PipelineWorkflowSaveRequest = {
  expectedRevision: number;
  document: PipelineWorkflowDocument;
};

export type PipelineWorkflowSaveResult =
  | { ok: true; record: PipelineWorkflowRecord }
  | {
      ok: false;
      failureClass: PipelineWorkflowSaveFailureClass;
      message: string;
      record?: PipelineWorkflowRecord;
    };

export class PipelineWorkflowSaveError extends Error {
  readonly failureClass: PipelineWorkflowSaveFailureClass;
  readonly record?: PipelineWorkflowRecord;

  constructor(failureClass: PipelineWorkflowSaveFailureClass, message: string, record?: PipelineWorkflowRecord) {
    super(message);
    this.name = "PipelineWorkflowSaveError";
    this.failureClass = failureClass;
    if (record) this.record = record;
  }
}

export function isPipelineWorkflowSaveFailureClass(value: unknown): value is PipelineWorkflowSaveFailureClass {
  return typeof value === "string" && (PIPELINE_WORKFLOW_SAVE_FAILURE_CLASSES as readonly string[]).includes(value);
}

export function pipelineWorkflowSaveFailureMessage(
  failureClass: PipelineWorkflowSaveFailureClass,
  detail?: string
): string {
  if (detail?.trim()) return detail.trim();
  if (failureClass === "invalid_pipeline_workflow") return "Workflow document is invalid.";
  if (failureClass === "pipeline_workflow_conflict") return "Another editor saved first. Reload and try again.";
  if (failureClass === "pipeline_workflow_network_failure") return "Network error while saving the workflow.";
  if (failureClass === "pipeline_workflow_load_failed") return "Could not load the saved workflow.";
  return "Workflow save failed. The previous version is still active.";
}

export function pipelineWorkflowHttpBody(
  record: PipelineWorkflowRecord
): PipelineWorkflowDocument & { revision: number } {
  return { ...record.document, revision: record.revision };
}

function stripWorkflowEnvelope(value: Record<string, unknown>): unknown {
  const { revision: _revision, expectedRevision: _expected, error: _error, message: _message, document, ...rest } = value;
  return document ?? rest;
}

export function parsePipelineWorkflowRecord(value: unknown): PipelineWorkflowRecord {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new PipelineWorkflowSaveError("invalid_pipeline_workflow", "invalid_pipeline_workflow");
  }
  const rec = value as Record<string, unknown>;
  const nested = rec.document != null ? rec.document : stripWorkflowEnvelope(rec);
  const revision = rec.revision == null ? 0 : rec.revision;
  if (typeof revision !== "number" || !Number.isInteger(revision) || revision < 0) {
    throw new PipelineWorkflowSaveError("invalid_pipeline_workflow", "invalid_pipeline_workflow");
  }
  try {
    return { revision, document: normalizePipelineWorkflow(nested) };
  } catch (error) {
    throw new PipelineWorkflowSaveError(
      "invalid_pipeline_workflow",
      error instanceof Error ? error.message : "invalid_pipeline_workflow"
    );
  }
}

export function parsePipelineWorkflowSaveRequest(value: unknown): PipelineWorkflowSaveRequest {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new PipelineWorkflowSaveError("invalid_pipeline_workflow", "invalid_pipeline_workflow");
  }
  const rec = value as Record<string, unknown>;
  if (typeof rec.expectedRevision !== "number" || !Number.isInteger(rec.expectedRevision) || rec.expectedRevision < 0) {
    throw new PipelineWorkflowSaveError("invalid_pipeline_workflow", "expectedRevision is required");
  }
  try {
    return {
      expectedRevision: rec.expectedRevision,
      document: normalizePipelineWorkflow(stripWorkflowEnvelope(rec))
    };
  } catch (error) {
    if (error instanceof PipelineWorkflowSaveError) throw error;
    throw new PipelineWorkflowSaveError(
      "invalid_pipeline_workflow",
      error instanceof Error ? error.message : "invalid_pipeline_workflow"
    );
  }
}

export function pipelineWorkflowSaveResultFromHttp(
  status: number,
  body: unknown,
  fallback: PipelineWorkflowSaveFailureClass = "pipeline_workflow_save_failed"
): PipelineWorkflowSaveResult {
  if (status >= 200 && status < 300) {
    return { ok: true, record: parsePipelineWorkflowRecord(body) };
  }
  const rec = body && typeof body === "object" && !Array.isArray(body) ? body as Record<string, unknown> : {};
  const failureClass = isPipelineWorkflowSaveFailureClass(rec.error)
    ? rec.error
    : status === 409
      ? "pipeline_workflow_conflict"
      : status === 400
        ? "invalid_pipeline_workflow"
        : fallback;
  let record: PipelineWorkflowRecord | undefined;
  try {
    if (rec.revision != null || rec.document != null || rec.steps != null) {
      record = parsePipelineWorkflowRecord(body);
    }
  } catch {
    record = undefined;
  }
  return {
    ok: false,
    failureClass,
    message: pipelineWorkflowSaveFailureMessage(
      failureClass,
      typeof rec.message === "string" ? rec.message : undefined
    ),
    ...(record ? { record } : {})
  };
}

export function pipelineWorkflowLaneIds(document: PipelineWorkflowDocument): string[] {
  return parsePipelineWorkflow(document).steps.map((step) => step.id);
}

export function addPipelineStep(
  document: PipelineWorkflowDocument,
  input: { afterId: string; step: PipelineStep }
): PipelineWorkflowDocument {
  const parsed = PipelineStepSchema.parse(input.step);
  const steps = [...document.steps];
  const index = steps.findIndex((step) => step.id === input.afterId);
  if (index < 0) throw new Error("pipeline_step_after_id_unknown");
  if (steps.some((step) => step.id === parsed.id)) throw new Error("pipeline_step_id_duplicate");
  steps.splice(index + 1, 0, parsed as PipelineStep);
  return parsePipelineWorkflow({ ...document, steps });
}

export function removePipelineStep(
  document: PipelineWorkflowDocument,
  stepId: string
): PipelineWorkflowDocument {
  if (document.steps.length <= 1) throw new Error("pipeline_step_last_required");
  const target = document.steps.find((step) => step.id === stepId);
  if (!target) throw new Error("pipeline_step_id_unknown");
  const role = pipelineStepRole(target);
  if (role) {
    throw new PipelineWorkflowSaveError("invalid_pipeline_workflow", pipelineRequiredRoleMissingMessage(role));
  }
  const steps = document.steps.filter((step) => step.id !== stepId);
  return parsePipelineWorkflow({ ...document, steps });
}

export function movePipelineStep(
  document: PipelineWorkflowDocument,
  stepId: string,
  toIndex: number
): PipelineWorkflowDocument {
  const steps = [...document.steps];
  const from = steps.findIndex((step) => step.id === stepId);
  if (from < 0) throw new Error("pipeline_step_id_unknown");
  const to = Math.trunc(toIndex);
  if (to < 0 || to >= steps.length || to === from) {
    return parsePipelineWorkflow({ ...document, steps });
  }
  const [moved] = steps.splice(from, 1);
  if (moved) steps.splice(to, 0, moved);
  return parsePipelineWorkflow({ ...document, steps });
}

export function movePipelineTool(
  document: PipelineWorkflowDocument,
  stepId: string,
  fromIndex: number,
  toIndex: number
): PipelineWorkflowDocument {
  const steps = document.steps.map((step) => {
    if (step.id !== stepId) return step;
    const tools = [...step.tools];
    const from = Math.trunc(fromIndex);
    const to = Math.trunc(toIndex);
    if (from < 0 || from >= tools.length || to < 0 || to >= tools.length) return step;
    const [moved] = tools.splice(from, 1);
    if (moved) tools.splice(to, 0, moved);
    return { ...step, tools };
  });
  return parsePipelineWorkflow({ ...document, steps });
}

export type PipelineWorkflowIdentityRow = WorkflowHouseFacts & { identityKey: string };

export function pipelineWorkflowAffectedIdentityKeys(
  rows: readonly PipelineWorkflowIdentityRow[],
  current: PipelineWorkflowDocument,
  next: PipelineWorkflowDocument,
  asOfDate: unknown = new Date().toISOString()
): string[] {
  const from = parsePipelineWorkflow(current);
  const to = parsePipelineWorkflow(next);
  const keys: string[] = [];
  for (const row of rows) {
    const identityKey = String(row.identityKey ?? "").trim();
    if (!identityKey) continue;
    const before = decideWorkflowHop(row, from, asOfDate);
    const after = decideWorkflowHop(row, to, asOfDate);
    if (
      before.action !== after.action
      || before.stepId !== after.stepId
      || before.failureClass !== after.failureClass
      || (before.nextWakeup ?? null) !== (after.nextWakeup ?? null)
    ) {
      keys.push(identityKey);
    }
  }
  return keys;
}

export function auctionWindowPolicyFromWorkflow(
  document: PipelineWorkflowDocument = getActivePipelineWorkflow()
): AuctionWindowPolicy {
  const step = requiredRole(parsePipelineWorkflow(document), "auction_window");
  return normalizeAuctionWindowPolicy({
    auctionWindowDays: step.gate?.auctionWindowDays ?? DEFAULT_AUCTION_WINDOW_POLICY.auctionWindowDays,
    dateRefreshCadence: step.gate?.dateRefreshCadence ?? DEFAULT_AUCTION_WINDOW_POLICY.dateRefreshCadence
  });
}

export function decideWorkflowHop(
  row: WorkflowHouseFacts,
  document: PipelineWorkflowDocument = getActivePipelineWorkflow(),
  asOfDate: unknown = new Date().toISOString()
): WorkflowHopDecision {
  const parsed = parsePipelineWorkflow(document);
  const misses = parseMisses(row.toolMisses ?? row.conveyorFailureClass);
  const policy = auctionWindowPolicyFromWorkflow(parsed);
  const windowStep = requiredRole(parsed, "auction_window");
  const holdStep = requiredRole(parsed, "hold");
  const asOf = dateOnlyIso(asOfDate);
  const spLookup = hasSuccessfulSpLookup(row.sourceFields);
  const auctionDate = spLookup
    ? resolveAuctionDate(row.sourceFields, asOfDate)
    : row.auctionDate;
  const eligibility = decideAuctionEligibility({
    auctionDate,
    sourceFields: row.sourceFields,
    asOfDate,
    policy
  });
  const timing = classifyAuctionTiming({
    auctionDate,
    asOfDate,
    auctionWindowDays: policy.auctionWindowDays
  });
  const liveness = auctionLivenessFromSourceFields(row.sourceFields, {
    saleDate: dateOnlyIso(auctionDate) ?? dateOnlyIso(row.auctionDate),
    asOfDate: asOf
  });

  if (spLookup && (liveness === "cancelled" || liveness === "complete")) {
    return terminal(namedStep(parsed, "closed") ?? holdStep, "on_hold", liveness === "cancelled" ? "sale_cancelled" : "sale_complete");
  }
  if (isBusinessOwnerText(row.ownerRaw) || isBusinessOwnerText(row.firstName) || isBusinessOwnerText(row.lastName)) {
    return terminal(namedStep(parsed, "closed") ?? holdStep, "on_hold", "invalid_owner_name");
  }
  const plvMiss = ecourtsSpPlvMissClass(row, misses);
  const plvStep = namedStep(parsed, "plv");
  if (plvMiss && plvStep) {
    return terminal(plvStep, "blocked", plvMiss);
  }
  const parkedMiss = auctionHouseMissClass(row, misses);
  if (parkedMiss && eligibility.reason !== "over_window" && timing !== "over_30_days") {
    if (wantsDueAuctionHouseMissRefresh(row, misses, asOfDate, policy)) {
      return hop(windowStep, "ecourts_auction_date", "ingested");
    }
    return {
      stepId: windowStep.id,
      toolId: null,
      action: "none",
      state: "ingested",
      failureClass: parkedMiss,
      nextWakeup: nextRefreshWakeup(
        auctionStatusCheckedAt(row) ?? auctionDateRefreshedAt(row),
        asOf ?? "",
        policy.dateRefreshCadence
      )
    };
  }
  if (
    spLookup
    && !isAuctionStillEnrichable(liveness)
    && eligibility.reason !== "over_window"
    && timing !== "over_30_days"
  ) {
    if (isPastUnknownStatusStale(row, asOfDate, policy)) {
      if (hasStoredEcourtsCase(row) && wantsEcourtsUpsert(row, windowStep)) {
        return hop(windowStep, "ecourts_case_upsert", "ingested");
      }
      const toolId = nextRefreshTool(windowStep, misses);
      if (toolId) {
        return {
          stepId: windowStep.id,
          toolId,
          action: TOOL_TO_ACTION[toolId],
          state: "ingested",
          failureClass: null
        };
      }
      if (wantsEcourtsUpsert(row, windowStep)) {
        return hop(windowStep, "ecourts_case_upsert", "ingested");
      }
      return terminal(windowStep, windowStep.miss === "block" ? "blocked" : "on_hold", "auction_date_refresh_pending");
    }
    return {
      stepId: windowStep.id,
      toolId: null,
      action: "none",
      state: "ingested",
      failureClass: "auction_status_unknown",
      nextWakeup: pastUnknownStatusWakeupDate(row, asOfDate)
        ?? nextRefreshWakeup(auctionStatusCheckedAt(row) ?? auctionDateRefreshedAt(row), asOf ?? "", policy.dateRefreshCadence)
    };
  }

  if (eligibility.reason === "invalid_sale_date" && !isAuctionStillEnrichable(liveness)) {
    return { ...terminal(holdStep, "on_hold", "invalid_sale_date"), nextWakeup: null };
  }
  if (timing === "past_or_unknown") {
    if (liveness === "cancelled" || liveness === "complete") {
      return terminal(namedStep(parsed, "closed") ?? holdStep, "on_hold", liveness === "cancelled" ? "sale_cancelled" : "sale_complete");
    }
    if (isPastUnknownStatusStale(row, asOfDate, policy)) {
      if (hasStoredEcourtsCase(row) && wantsEcourtsUpsert(row, windowStep)) {
        return hop(windowStep, "ecourts_case_upsert", "ingested");
      }
      const toolId = nextRefreshTool(windowStep, misses);
      if (toolId) {
        return {
          stepId: windowStep.id,
          toolId,
          action: TOOL_TO_ACTION[toolId],
          state: "ingested",
          failureClass: null
        };
      }
      if (wantsEcourtsUpsert(row, windowStep)) {
        return hop(windowStep, "ecourts_case_upsert", "ingested");
      }
      return terminal(windowStep, windowStep.miss === "block" ? "blocked" : "on_hold", "auction_date_refresh_pending");
    }
    if (!isAuctionStillEnrichable(liveness)) {
      return {
        stepId: windowStep.id,
        toolId: null,
        action: "none",
        state: "ingested",
        failureClass: "past_or_unknown",
        nextWakeup: pastUnknownStatusWakeupDate(row, asOfDate)
          ?? nextRefreshWakeup(auctionStatusCheckedAt(row) ?? auctionDateRefreshedAt(row), asOf ?? "", policy.dateRefreshCadence)
      };
    }
  }
  if (eligibility.reason === "over_window") {
    const waiting = namedStep(parsed, "waiting") ?? windowStep;
    return {
      stepId: waiting.id,
      toolId: null,
      action: "none",
      state: "ingested",
      failureClass: "over_30_days",
      nextWakeup: eligibility.nextWakeup
    };
  }

  if (wantsEcourtsUpsert(row, windowStep)) {
    return hop(windowStep, "ecourts_case_upsert", "ingested");
  }

  const retryable = retryableRetrievalEvidenceClass(row, misses);
  if (retryable && registeredToolCanHop("ecourts_auction_date")) {
    return hop(windowStep, "ecourts_auction_date", "ingested");
  }

  for (const step of parsed.steps) {
    if (step.kind === "ingest" || step.kind === "terminal") continue;
    if (step.kind === "gate" && step.gate?.auctionWindowDays != null) continue;

    if (pipelineStepHasFallbackGroup(step, "owner")) {
      if (!hasPersonName(row)) {
        const selected = selectOwnerLookupTool({
          tools: step.tools,
          toolMisses: misses,
          now: String(asOfDate ?? "")
        });
        if (selected.toolId) return hop(step, selected.toolId, "needs_owner");
        if (selected.reason === "provider_paused") {
          return {
            stepId: step.id,
            toolId: null,
            action: "none",
            state: "needs_owner",
            failureClass: selected.failureClass,
            nextWakeup: selected.nextAttemptAt ?? null
          };
        }
        return terminal(step, "blocked", selected.failureClass ?? "blocked_no_owner");
      }
      continue;
    }

    if (pipelineStepHasFallbackGroup(step, "skip")) {
      if (!hasSmartSkipResult(row.skipPayload)) {
        const toolId = nextOpenTool(step, (id) => toolOutcome(id, row, misses, asOfDate));
        if (toolId) {
          return { ...hop(step, toolId, "skip_ready"), failureClass: "awaiting_skip_trace" };
        }
        return terminal(step, "blocked", "skip_row_absent");
      }
      continue;
    }

    if (pipelineStepHasFallbackGroup(step, "value")) {
      const toolId = nextOpenTool(step, (id) => toolOutcome(id, row, misses, asOfDate));
      if (toolId) return hop(step, toolId, "skip_complete");
      if (marketValue(row, step.tools) == null) {
        return terminal(step, step.miss === "hold" ? "on_hold" : "blocked", "blocked_no_value");
      }
      continue;
    }

    if (pipelineStepHasFallbackGroup(step, "plv")) {
      if (finiteNumber(row.plv) != null) continue;
      if (String(row.conveyorFailureClass ?? "") === "blocked_no_plv") {
        return terminal(step, "blocked", "blocked_no_plv");
      }
      const toolId = nextOpenTool(step, (id) => toolOutcome(id, row, misses, asOfDate));
      if (toolId) return hop(step, toolId, "skip_complete");
      if (finiteNumber(row.plv) == null) {
        const miss = step.tools.map((id) => misses[id]).find(Boolean) ?? "blocked_no_plv";
        if (isTransientEcourtsPlvEvidenceClass(miss)) {
          return hop(step, "ecourts_sp_plv", "skip_complete");
        }
        return terminal(step, "blocked", miss);
      }
      continue;
    }

    if (pipelineStepHasFallbackGroup(step, "equity")) {
      const policy = equityPolicyFromSteps(parsed.steps);
      const resolved = resolvePropertyValue({
        zestimate: row.zestimate,
        attomAvm: row.attomAvm,
        preference: policy.preference
      });
      const valueKind = resolved.source === "attom_avm" ? "attom_avm" : "zillow_zestimate";
      const calculated = calculatePropertyEquity({
        zestimate: row.zestimate,
        attomAvm: row.attomAvm,
        plv: row.plv,
        policy,
        valueRunId: propertyObservationFromSourceFields(row.sourceFields, valueKind)?.runId ?? null,
        plvRunId: propertyObservationFromSourceFields(row.sourceFields, "plv")?.runId ?? null
      });
      if (shouldRecomputeEquity({
        estimatedEquity: row.estimatedEquity,
        stored: equityProvenanceFromSourceFields(row.sourceFields),
        calculated
      })) {
        return hop(step, "equity_formula", "valued");
      }
      continue;
    }

    if (step.tools.some((tool) => isObituarySearchToolId(tool))) {
      const policy = obituaryStepPolicyFromWorkflow(parsed);
      if (policy.disabled) continue;
      const obit = normalizeStatus(row.obituaryMatchStatus);
      const kind = classifyObituaryOutcome(automaticObituaryPropertyMatch({ matchStatus: obit }) ?? obit);
      if (CONFIRMED_OBIT.has(obit)) continue;
      if (kind === "valid_no_match" || kind === "insufficient_evidence") {
        const toolId = nextOpenTool(step, (id) => toolOutcome(id, row, misses));
        if (toolId && kind === "insufficient_evidence") return hop(step, toolId, "valued");
        continue;
      }
      if (kind === "provider_failure" || kind === "retryable_error") {
        const toolId = nextOpenTool(step, (id) => toolOutcome(id, row, misses));
        if (toolId) return hop(step, toolId, "valued");
        return terminal(step, "on_hold", obituaryPlannerFailureClass(kind) ?? kind);
      }
      const toolId = nextOpenTool(step, (id) => toolOutcome(id, row, misses));
      if (toolId) return hop(step, toolId, "valued");
      if (!policy.requiredForClose) continue;
      return terminal(
        step,
        "on_hold",
        step.tools.map((id) => misses[id]).find(Boolean) ?? "obituary_insufficient_evidence"
      );
    }

    if (pipelineStepHasFallbackGroup(step, "obituary_evidence") || pipelineStepHasFallbackGroup(step, "obituary_identity")) {
      continue;
    }

    if (pipelineStepHasFallbackGroup(step, "intestacy")) {
      const policy = heirEstimatePolicyFromWorkflow(parsed);
      if (policy.disabled || !registeredToolCanHop("nc_intestacy_text_tree")) continue;
      // Optional automatic step. The conveyor drain schedules it; Close does not wait.
      continue;
    }

    if (pipelineStepHasFallbackGroup(step, "close")) {
      const closeId = String(row.closeLeadId ?? "").trim();
      if (closeId) continue;
      const decision = decideCloseEligibility(row, closeEligibilityPolicyFromWorkflow(parsed));
      if (decision.action === "create") return hop(step, "close_create", "gated_pass");
      if (decision.action === "patch") continue;
      return terminal(step, "gated_block", decision.failureClass);
    }
  }

  const obit = normalizeStatus(row.obituaryMatchStatus);
  const obituaryPolicy = obituaryStepPolicyFromWorkflow(parsed);
  if (CONFIRMED_OBIT.has(obit)) {
    return closeStatusDelivery(parsed, row, {
      confirmedState: "ready_to_call",
      action: "status_ready",
      failureClass: null
    });
  }
  if (HOLD_OBIT.has(obit)) {
    return closeStatusDelivery(parsed, row, {
      confirmedState: "on_hold",
      action: "status_hold",
      failureClass: obit === "ambiguous_review" ? "obituary_insufficient_evidence" : "obituary_no_match"
    });
  }
  if (classifyObituaryOutcome(obit) === "insufficient_evidence") {
    if (obituaryPolicy.requiredForClose) {
      return closeStatusDelivery(parsed, row, {
        confirmedState: "on_hold",
        action: "status_hold",
        failureClass: "obituary_insufficient_evidence"
      });
    }
    return closeStatusDelivery(parsed, row, {
      confirmedState: "ready_to_call",
      action: "status_ready",
      failureClass: null
    });
  }

  return {
    stepId: requiredRole(parsed, "blocked").id,
    toolId: null,
    action: "none",
    state: "gated_block",
    failureClass: row.conveyorFailureClass ? String(row.conveyorFailureClass) : "blocked_no_equity"
  };
}

function closeStatusDelivery(
  parsed: PipelineWorkflowDocument,
  row: WorkflowHouseFacts,
  input: {
    confirmedState: "ready_to_call" | "on_hold";
    action: "status_ready" | "status_hold";
    failureClass: string | null;
  }
): WorkflowHopDecision {
  const already = row.conveyorState === input.confirmedState;
  if (already) {
    const role: PipelineRequiredRole = input.confirmedState === "ready_to_call" ? "ready" : "hold";
    return {
      stepId: requiredRole(parsed, role).id,
      toolId: null,
      action: "none",
      state: input.confirmedState,
      failureClass: input.failureClass
    };
  }
  return {
    stepId: requiredRole(parsed, "close").id,
    toolId: "close_status",
    action: input.action,
    state: "close_pushed",
    failureClass: input.failureClass
  };
}

function requiredRole(parsed: PipelineWorkflowDocument, role: PipelineRequiredRole): PipelineStep {
  const step = pipelineStepForRole(parsed, role);
  if (!step) {
    throw new PipelineWorkflowSaveError("invalid_pipeline_workflow", pipelineRequiredRoleMissingMessage(role));
  }
  return step;
}

function namedStep(parsed: PipelineWorkflowDocument, id: string): PipelineStep | null {
  return parsed.steps.find((step) => step.id === id) ?? null;
}

function hop(step: PipelineStep, toolId: RegisteredToolId, state: string): WorkflowHopDecision {
  return { stepId: step.id, toolId, action: TOOL_TO_ACTION[toolId], state, failureClass: null };
}

function terminal(step: PipelineStep, state: string, failureClass: string): WorkflowHopDecision {
  return { stepId: step.id, toolId: null, action: "none", state, failureClass };
}

export function isAuctionDateRefreshDue(
  row: WorkflowHouseFacts,
  step: PipelineStep,
  asOfDate: unknown
): boolean {
  const cadence = step.gate?.dateRefreshCadence ?? "weekly";
  return isAuctionRefreshDue(auctionDateRefreshedAt(row), String(asOfDate ?? ""), cadence);
}

/** Past/unknown sales need a fresh SP eCourts status before owner/skip/value work. */
export function isPastUnknownStatusStale(
  row: WorkflowHouseFacts,
  asOfDate: unknown,
  _policy: AuctionWindowPolicy = DEFAULT_AUCTION_WINDOW_POLICY
): boolean {
  const last = auctionStatusCheckedAt(row) ?? auctionDateRefreshedAt(row);
  if (!last) return true;
  const fields = parseJsonObject(row.sourceFields);
  const ecourts = parseJsonObject(fields.ecourts);
  const details = parseJsonObject(ecourts.auctionStatusDetails);
  return isAuctionSourceCheckDue({
    status: String(details.status ?? ecourts.auctionStatus ?? details.saleState ?? ""),
    saleDate: dateOnlyIso(row.auctionDate) ?? dateOnlyIso(ecourts.auctionDate),
    deadlineDate: dateOnlyIso(details.deadlineDate ?? ecourts.upsetDeadline),
    lastSuccessfulSourceCheckAt: last,
    sourceCheckedAt: last,
    asOfDate: String(asOfDate ?? "")
  });
}

/** Next source-check day from canonical cadence, not a fixed seven-day clock. */
export function pastUnknownStatusWakeupDate(row: WorkflowHouseFacts, asOfDate?: unknown): string | null {
  const last = auctionStatusCheckedAt(row) ?? auctionDateRefreshedAt(row);
  if (!last) return null;
  const fields = parseJsonObject(row.sourceFields);
  const ecourts = parseJsonObject(fields.ecourts);
  const details = parseJsonObject(ecourts.auctionStatusDetails);
  const next = nextAuctionSourceCheckAt({
    status: String(details.status ?? ecourts.auctionStatus ?? ""),
    saleDate: dateOnlyIso(row.auctionDate) ?? dateOnlyIso(ecourts.auctionDate),
    deadlineDate: dateOnlyIso(details.deadlineDate ?? ecourts.upsetDeadline),
    lastSuccessfulSourceCheckAt: last,
    asOfDate: String(asOfDate ?? last)
  });
  return next ? next.slice(0, 10) : null;
}

function auctionDateRefreshedAt(row: WorkflowHouseFacts): string | null {
  const direct = dateOnlyIso(row.auctionDateRefreshedAt);
  if (direct) return direct;
  const fields = parseJsonObject(row.sourceFields);
  const ecourts = parseJsonObject(fields.ecourts);
  return dateOnlyIso(ecourts.auctionDateRefreshedAt ?? fields.auctionDateRefreshedAt);
}

function auctionStatusCheckedAt(row: WorkflowHouseFacts): string | null {
  const fields = parseJsonObject(row.sourceFields);
  const ecourts = parseJsonObject(fields.ecourts);
  return dateOnlyIso(ecourts.auctionStatusCheckedAt ?? fields.auctionStatusCheckedAt);
}

function registeredToolCanHop(toolId: RegisteredToolId): boolean {
  const availability = pipelineToolAvailability(toolId);
  if (availability === "unavailable" || availability === "disabled") return false;
  return pipelineToolDefinition(toolId)?.executor.kind !== "none";
}

function isRetrievableAuctionHouseMiss(value: unknown): boolean {
  return value === ECOURTS_AUCTION_DATE_MISS || value === ECOURTS_AUCTION_NO_RESULT;
}

function auctionHouseMissClass(row: WorkflowHouseFacts, misses: Record<string, string>): string | null {
  const stamped = misses.ecourts_auction_date;
  if (stamped && isRetrievableAuctionHouseMiss(stamped)) return stamped;
  if (isRetrievableAuctionHouseMiss(row.conveyorFailureClass)) return String(row.conveyorFailureClass);
  return null;
}

/** Typed Analysis PLV miss (`ecourts_sp_plv_*_miss`). Not `ecourts_auction_no_result`. Skip when PLV is already set. */
function isEcourtsSpPlvMiss(value: unknown): boolean {
  return typeof value === "string" && value.startsWith("ecourts_sp_plv") && value.endsWith("_miss");
}

function ecourtsSpPlvMissClass(row: WorkflowHouseFacts, misses: Record<string, string>): string | null {
  if (finiteNumber(row.plv) != null) return null;
  if (isEcourtsSpPlvMiss(misses.ecourts_sp_plv)) return String(misses.ecourts_sp_plv);
  if (isEcourtsSpPlvMiss(row.conveyorFailureClass)) return String(row.conveyorFailureClass);
  return null;
}

function isRetryableRetrievalMiss(value: unknown): boolean {
  return value === ECOURTS_MISSING_REQUIRED_PDFS
    || value === ECOURTS_COLLECTION_PARTIAL
    || value === ECOURTS_PAGE_INACCESSIBLE
    || value === ECOURTS_BROWSER_LEASE_EXPIRED;
}

function retryableRetrievalEvidenceClass(row: WorkflowHouseFacts, misses: Record<string, string>): string | null {
  const stamped = misses.ecourts_auction_date || misses.ecourts_sp_plv || String(row.conveyorFailureClass ?? "");
  return isRetryableRetrievalMiss(stamped) ? stamped : null;
}



function wantsDueAuctionHouseMissRefresh(
  row: WorkflowHouseFacts,
  misses: Record<string, string>,
  asOfDate: unknown,
  policy: AuctionWindowPolicy
): boolean {
  if (!auctionHouseMissClass(row, misses)) return false;
  return isAuctionRefreshDue(
    auctionStatusCheckedAt(row) ?? auctionDateRefreshedAt(row),
    String(asOfDate ?? ""),
    policy.dateRefreshCadence
  );
}

function nextRefreshTool(step: PipelineStep, misses: Record<string, string>): RegisteredToolId | null {
  for (const toolId of step.tools) {
    if (TOOL_TO_ACTION[toolId] !== "auction_date_refresh") continue;
    if (!registeredToolCanHop(toolId)) continue;
    const miss = misses[toolId];
    if (!miss || isRetrievableAuctionHouseMiss(miss) || isRetryableRetrievalMiss(miss)) return toolId;
  }
  return null;
}

function hasStoredEcourtsCase(row: WorkflowHouseFacts): boolean {
  const ecourts = parseJsonObject(parseJsonObject(row.sourceFields).ecourts);
  if (String(ecourts.caseId ?? "").trim()) return true;
  const events = ecourts.caseEvents;
  return Array.isArray(events) && events.length > 0;
}

function wantsEcourtsUpsert(row: WorkflowHouseFacts, windowStep: PipelineStep): boolean {
  return ecourtsProjectionPending(row)
    && windowStep.tools.includes("ecourts_case_upsert")
    && registeredToolCanHop("ecourts_case_upsert");
}

function ecourtsProjectionPending(row: WorkflowHouseFacts): boolean {
  const ecourts = parseJsonObject(parseJsonObject(row.sourceFields).ecourts);
  if (String(ecourts.projectionVersion ?? "").trim()) return false;
  const awaitingWrite = row.conveyorFailureClass === "ecourts_upsert_pending"
    || Boolean(String(ecourts.caseId ?? "").trim() || String(ecourts.auctionStatusCheckedAt ?? "").trim());
  if (!awaitingWrite) return false;
  if (hasPersonName(row)) return false;
  if (hasSmartSkipResult(row.skipPayload)) return false;
  if (finiteNumber(row.plv) != null || finiteNumber(row.zestimate) != null || finiteNumber(row.attomAvm) != null) return false;
  if (String(row.closeLeadId ?? "").trim()) return false;
  return true;
}

function parseJsonObject(value: unknown): Record<string, unknown> {
  if (value && typeof value === "object" && !Array.isArray(value)) return value as Record<string, unknown>;
  if (typeof value !== "string" || !value.trim()) return {};
  try {
    const parsed = JSON.parse(value) as unknown;
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed as Record<string, unknown> : {};
  } catch {
    return {};
  }
}

function dateOnlyIso(value: unknown): string | null {
  const text = String(value ?? "").trim().slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(text) ? text : null;
}

function nextOpenTool(
  step: PipelineStep,
  outcome: (id: RegisteredToolId) => "hit" | "miss" | "open"
): RegisteredToolId | null {
  for (const toolId of step.tools) {
    if (!registeredToolCanHop(toolId)) continue;
    if (outcome(toolId) === "hit") return null;
  }
  for (const toolId of step.tools) {
    if (!registeredToolCanHop(toolId)) continue;
    if (outcome(toolId) === "open") return toolId;
  }
  return null;
}

function toolOutcome(
  toolId: RegisteredToolId,
  row: WorkflowHouseFacts,
  misses: Record<string, string>,
  asOfDate: unknown = undefined
): "hit" | "miss" | "open" {
  if (toolId === "attom_avm" || toolId === "zillow_zestimate" || toolId === "ecourts_sp_plv") {
    if (misses[toolId] && isPipelineServiceFailureClass(misses[toolId])) return "open";
    if (toolId === "ecourts_sp_plv" && isTransientEcourtsPlvEvidenceClass(misses[toolId])) return "open";
    return propertyObservationToolOutcome({
      toolId,
      value: toolId === "attom_avm" ? row.attomAvm : toolId === "zillow_zestimate" ? row.zestimate : row.plv,
      sourceFields: row.sourceFields,
      miss: misses[toolId] ?? null,
      asOf: String(asOfDate ?? "")
    });
  }
  if (toolId === "ecourts_case_upsert") return ecourtsProjectionPending(row) ? "open" : "hit";
  if (misses[toolId]) return isPipelineServiceFailureClass(misses[toolId]) ? "open" : "miss";
  switch (toolId) {
    case "reportall_owner":
    case "attom_owner":
      return hasPersonName(row) ? "hit" : "open";
    case "smartskip":
      return hasSmartSkipResult(row.skipPayload) ? "hit" : "open";
    case "equity_formula":
      return finiteNumber(row.estimatedEquity) != null ? "hit" : "open";
    case "close_create":
      return String(row.closeLeadId ?? "").trim() ? "hit" : "open";
    case "obituary_google":
    case "obituary_xai": {
      const status = normalizeStatus(row.obituaryMatchStatus);
      if (CONFIRMED_OBIT.has(status) || HOLD_OBIT.has(status)) return "hit";
      const kind = classifyObituaryOutcome(status);
      if (kind === "insufficient_evidence" || kind === "provider_failure" || kind === "retryable_error") {
        return misses[toolId] ? "miss" : "hit";
      }
      return "open";
    }
    case "obituary_evidence":
    case "obituary_identity":
      return CONFIRMED_OBIT.has(normalizeStatus(row.obituaryMatchStatus)) || HOLD_OBIT.has(normalizeStatus(row.obituaryMatchStatus))
        ? "hit"
        : "open";
    case "nc_intestacy_text_tree":
      return heirEstimateStatusFromFacts(row) === "open" ? "open" : "hit";
    case "ecourts_auction_date":
    case "brightdata_ecourts_auction_date":
      return misses[toolId] ? "miss" : "open";
    default:
      return "open";
  }
}

export function marketValue(row: WorkflowHouseFacts, toolOrder: readonly RegisteredToolId[]): number | null {
  return resolvePropertyValue({
    zestimate: row.zestimate,
    attomAvm: row.attomAvm,
    preference: toolOrder
  }).value ?? finiteNumber(row.propertyValue);
}

function parseMisses(value: unknown): Record<string, string> {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .filter(([key, miss]) => isRegisteredToolId(key) && typeof miss === "string")
        .map(([key, miss]) => [key, String(miss)])
    );
  }
  if (typeof value === "string" && value.includes("attom_avm")) return { attom_avm: value };
  if (typeof value === "string" && /zillow|property_value|property_found|property_address/.test(value)) {
    return { zillow_zestimate: value };
  }
  if (typeof value === "string" && isRetrievableAuctionHouseMiss(value)) {
    return { ecourts_auction_date: value };
  }
  return {};
}

function hasPersonName(row: WorkflowHouseFacts): boolean {
  return isPersonOwner({ firstName: row.firstName, lastName: row.lastName, ownerRaw: row.ownerRaw });
}

function text(value: unknown): string {
  return value == null ? "" : String(value).replaceAll(/\s+/g, " ").trim();
}

function normalizeStatus(value: unknown): string {
  return text(value).toLowerCase();
}
